/** @format */
import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { SessionStorageService } from 'sis-common';

/** ZoomLevel indicates how many study years are visible in the timeline at a time (i.e. zooming in decreases the value) */

export enum ZoomLevel {
    SEVEN_VISIBLE_STUDY_YEARS = 7,
    FIVE_VISIBLE_STUDY_YEARS = 5,
    THREE_VISIBLE_STUDY_YEARS = 3,
    TWO_VISIBLE_STUDY_YEARS = 2,
    ONE_VISIBLE_STUDY_YEAR = 1,
}

const ZOOM_LEVELS: ZoomLevel[] = Object.values(ZoomLevel).filter(value => typeof value !== 'string');

const DEFAULT_ZOOM_LEVEL = ZoomLevel.THREE_VISIBLE_STUDY_YEARS;
const SESSION_STORAGE_KEY = 'timeline.zoomLevel';

@Injectable()
export class TimelineZoomService {
    private readonly sessionStorageService = inject(SessionStorageService);

    private readonly _zoomLevel$: BehaviorSubject<ZoomLevel>;
    readonly zoomLevel$: Observable<ZoomLevel>;

    constructor() {
        const storedZoomLevel = this.sessionStorageService.getValueOrDefault(SESSION_STORAGE_KEY, null);
        const initialZoomLevel: ZoomLevel = ZOOM_LEVELS.includes(storedZoomLevel) ? storedZoomLevel : DEFAULT_ZOOM_LEVEL;
        this._zoomLevel$ = new BehaviorSubject(initialZoomLevel);
        this.zoomLevel$ = this._zoomLevel$.asObservable();
    }

    zoomIn(): void {
        const newZoomLevel = ZOOM_LEVELS.find(level => level < this._zoomLevel$.value);
        if (newZoomLevel) {
            this._zoomLevel$.next(newZoomLevel);
            this.sessionStorageService.setValue(SESSION_STORAGE_KEY, this._zoomLevel$.value);
        }
    }

    zoomOut(): void {
        const newZoomLevel = ZOOM_LEVELS.findLast(level => level > this._zoomLevel$.value);
        if (newZoomLevel) {
            this._zoomLevel$.next(newZoomLevel);
            this.sessionStorageService.setValue(SESSION_STORAGE_KEY, this._zoomLevel$.value);
        }
    }
}
